import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  SsoIntegration,
  useSsoIntegrationActivateMutation,
  useSsoIntegrationDeactivateMutation,
  useSsoIntegrationDestroyMutation,
} from 'graphql-common';
import toast from '@lib/components/Toast/Toast';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import onDestroyListItemCallBack from '@lib/utils/onDestroyListItemCallBack';
import useSearchParam from '@lib/hooks/useSearchParam';
import {
  Modals,
  UseModalManagementResult,
} from '@lib/hooks/useModalManagement';
import useSsoIntegrationsQueryHook from 'utils/fetch/hooks/useSsoIntegrationsQueryHook';
import { APP_URLS } from 'constants/urls';

export default function useActions(props: {
  useModalManagementResult: UseModalManagementResult;
}) {
  const { useModalManagementResult } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const [searchQuery] = useSearchParam();
  const [paginationParams] = usePaginationParams();
  const { closeModal, openModal } = useModalManagementResult;

  const [selectedItem, setSelectedItem] = useState<SsoIntegration | null>(null);

  const ssoIntegrationsQueryHookResult = useSsoIntegrationsQueryHook({
    searchQuery,
    paginationParams,
    orderingParams: paginationParams,
  });

  const [activateMutation] = useSsoIntegrationActivateMutation({
    onCompleted: () => {
      ssoIntegrationsQueryHookResult.refetch();
      toast({ content: t('activate-sso-connection-success') });
    },
    onError: (error) => {
      toast({ content: error.message || t('something-went-wrong') });
    },
  });

  const [deactivateMutation] = useSsoIntegrationDeactivateMutation({
    onCompleted: () => {
      ssoIntegrationsQueryHookResult.refetch();
      toast({ content: t('deactivate-sso-connection-success') });
    },
    onError: (error) => {
      toast({ content: error.message || t('something-went-wrong') });
    },
  });

  const [destroyMutation] = useSsoIntegrationDestroyMutation({
    onCompleted: () => {
      closeModal(Modals.SsoIntegrationDestroyModal);
      toast({ content: t('destroy-sso-connection-success') });
      onDestroyListItemCallBack({
        collection: ssoIntegrationsQueryHookResult.collection,
        navigate,
        pathname,
        refetch: ssoIntegrationsQueryHookResult.refetch,
        search,
        totalPages: ssoIntegrationsQueryHookResult.totalPages,
      });
    },
    onError: (error) => {
      toast({ content: error.message || t('something-went-wrong') });
    },
  });

  const onCreate = () => {
    navigate(APP_URLS.dashboard.sso.add.path);
  };

  const onView = (item?: SsoIntegration) => {
    if (item?.id) {
      navigate(
        APP_URLS.dashboard.sso.view.getDynamicPath({
          pathParts: { id: item.id },
        }),
      );
    }
  };

  const onEdit = (item?: SsoIntegration) => {
    if (item?.id) {
      navigate(
        APP_URLS.dashboard.sso.edit.getDynamicPath({
          pathParts: { id: item.id },
        }),
      );
    }
  };

  const onActivate = (item?: SsoIntegration) => {
    if (item?.id) {
      activateMutation({ variables: { id: item.id } });
    }
  };

  const onDeactivate = (item?: SsoIntegration) => {
    if (item?.id) {
      deactivateMutation({ variables: { id: item.id } });
    }
  };

  const onDelete = (item?: SsoIntegration) => {
    if (item?.id) {
      setSelectedItem(item);
      openModal(Modals.SsoIntegrationDestroyModal);
    }
  };

  const onConfirmedDelete = () => {
    closeModal(Modals.SsoIntegrationDestroyModal);
    if (selectedItem?.id) {
      destroyMutation({
        variables: {
          id: selectedItem.id,
        },
      });
    }
  };

  return {
    ssoIntegrationsQueryHookResult,
    onConfirmedDelete,
    onActivate,
    onCreate,
    onDeactivate,
    onDelete,
    onEdit,
    onView,
  };
}
